/**
 * Pure helpers for privacy session settings — kept free of Redux so tests and
 * the persist migrate path share one source of truth.
 */

export type PrivacySettings = {
  privacyBlurEnabled: boolean;
  privacyPersonDetectionEnabled: boolean;
};

/** Pilot dash: ROI blur always on, person boxes always off. Not user-toggled. */
export const DASH_PRIVACY_BLUR_ENABLED = true;
export const DASH_PRIVACY_PERSON_DETECTION_ENABLED = false;

/**
 * Map a persisted session fragment onto dash privacy flags.
 * Older combined / split toggles are discarded so a stored "detect on" cannot
 * come back after the settings menu controls were removed.
 */
export function migratePrivacySettings(_state: {
  privacyVideoEnabled?: boolean;
  privacyBlurEnabled?: boolean;
  privacyPersonDetectionEnabled?: boolean;
} | null | undefined): PrivacySettings {
  return {
    privacyBlurEnabled: DASH_PRIVACY_BLUR_ENABLED,
    privacyPersonDetectionEnabled: DASH_PRIVACY_PERSON_DETECTION_ENABLED,
  };
}

/** Overlay should be mounted when either feature is on. */
export function privacyProcessingEnabled(
  blurEnabled: boolean,
  personDetectionEnabled: boolean
): boolean {
  return blurEnabled || personDetectionEnabled;
}

/**
 * CSS blur on the raw <img> is only a fallback after a cyan ROI was confirmed
 * and the worker overlay is currently down (stall / error), not peeking.
 */
export function shouldApplyCssBlurFallback(opts: {
  blurEnabled: boolean;
  roiConfirmed: boolean;
  blurActive: boolean;
  overlayVisible: boolean;
  peeking: boolean;
}): boolean {
  return (
    opts.blurEnabled &&
    opts.roiConfirmed &&
    !opts.blurActive &&
    !opts.overlayVisible &&
    !opts.peeking
  );
}

/**
 * Worker paints ROI blur only when blur is enabled and a cyan lock is present
 * (and layout is known). Otherwise the frame is raw, optionally with boxes.
 * A lock requires a large ROI ring (radius ~12–55% of the feed short side);
 * small reticles do not count — see `CIRCLE_ROI_*_RADIUS_RATIO` in config.
 */
export function shouldApplyRoiBlur(opts: {
  blurEnabled: boolean;
  hasCircle: boolean;
  hasLayout: boolean;
}): boolean {
  return opts.blurEnabled && opts.hasCircle && opts.hasLayout;
}
